import { z } from "zod";

/**
 * `POST /api/v1/knock/start` — PRD §5.3.
 *
 * Deliberately permissive on the *semantic* fields. The §5.3 ordering puts the
 * door checks (blocklist, cooldown, caps, rate limit) ahead of "is the ceiling
 * high enough" and "did you accept the terms", so a blocked sender who also
 * forgot the terms box must still be told the door is closed — never given a
 * validation hint about a door they cannot knock at. Anything checked *in* that
 * order therefore leaves this schema and lives in `knocks/start.ts`; what stays
 * here is only what is malformed regardless of which door it was aimed at.
 */

/** FR-3.3: captured once at the first knock, reused everywhere afterwards. */
export const SenderProfileInput = z.object({
  reply_to_email: z.string().trim().max(320).optional(),
  // Unstructured (owner decision 2026-08-05): "my telegram is @x" is a way to
  // be reached. Kept beside the email, not instead of it — email is still the
  // one channel the relay can DELIVER to; this one the recipient reads.
  reply_contact: z.string().trim().max(300).optional(),
  booking_url: z.string().trim().max(500).optional(),
  company_url: z.string().trim().max(500).optional(),
  // `phone`, `whatsapp` and `telegram` are GONE (ruling R-0828-2). They were the
  // extras a door's House Rules could ask a knocker for, one intake question
  // each; the console is the only channel people talk to people in, so a door
  // asks for none of them and no client sends them. `reply_contact` above is a
  // different lane and stays: it is how a sender answers "where can you be
  // reached" without an email, not a channel the door requested.
});
export type SenderProfileInput = z.infer<typeof SenderProfileInput>;

/**
 * `POST /api/v1/knock/{id}/turn` — PRD §5.4.
 *
 * The 2,000-character rule (E-95) deliberately does NOT live here. It is a
 * §6.2 L3 pre-filter, applied after NFKC normalisation, and it must produce the
 * same refusal whether the sender sent 2,001 plain characters or 1,900 that
 * expand past the cap when composed — which a schema on the raw string cannot
 * see. What `max` is doing here is a transport bound, not the rule: a body this
 * far past the cap is not a message, and parsing it is work we decline to do.
 */
export const KnockTurnRequest = z.object({
  // Emptiness is NOT rejected here: the §6.2 L3 pre-filter owns "a message is
  // required" — it must fire the same on "" and on whitespace, which a string
  // `min` cannot see. `max` stays as the transport bound: a body this far past
  // the 2,000 cap is not a message worth parsing.
  message: z.string().max(20_000).optional(),
  /**
   * A VOICE NOTE, IN PLACE OF `message` (wave 76). The id the upload's 201
   * answered with; the turn's words are that note's transcript. Never both — a
   * turn is one thing said, and the route refuses the pair.
   */
  voice_attachment_id: z.string().uuid().optional(),
  /** Client-supplied, echoed nowhere: a handle for the sender's own retry logic. */
  client_turn_id: z.string().trim().max(64).optional(),
});
export type KnockTurnRequest = z.infer<typeof KnockTurnRequest>;

/**
 * `POST /api/v1/knock/{id}/messages` — the sender writing back, once the door
 * has opened (owner, 2026-08-26).
 *
 * The bound is a plain `max` and the emptiness rule IS here, unlike the turn
 * above, and the difference is the whole point of this endpoint: no model reads
 * this, so there is no §6.2 pre-filter downstream to own "a message is
 * required", and no composition rule for a cap to be defeated by. It is a
 * message between two people, and the only things that can be wrong with it are
 * that it is blank or that it is absurd.
 */
export const KnockWriteBackRequest = z.object({
  message: z.string().trim().min(1).max(4_000),
});
export type KnockWriteBackRequest = z.infer<typeof KnockWriteBackRequest>;

export const KnockStartRequest = z.object({
  door_handle: z.string().trim().min(1).max(64),
  /**
   * REFUSED, not ignored (ruling C3). There is no ceiling to set and no door
   * that sets one: a knock draws on the KNOCKER's own plan, and what varies is
   * how much work the knock needs rather than whose door it arrived at. A
   * client still sending this field is working from the old contract, and
   * answering 201 to it would silently accept a number that client believes is
   * a limit — so it fails the parse and the §5.3 order never runs.
   */
  ceiling_credits: z
    .never("there is no ceiling: a knock draws on your own plan — remove this field")
    .optional(),
  sender_profile: SenderProfileInput.optional(),
  attachments: z.array(z.string().trim().max(64)).max(10).optional(),
  /** FR-5.4. Checked after the door checks, hence optional here. */
  accept_terms: z.boolean().optional(),
  /**
   * The anonymous key a held draft was written under, when this knock came out
   * of one. PROVENANCE ONLY — the text still arrives in the ordinary body; a key
   * that names no live draft is not an error, it is a knock with no history.
   */
  anon_key: z.uuid().optional(),
  /**
   * THE KNOCKER'S OWN WORDS FOLLOW AS THE FIRST TURN (wave 82). The console's
   * compose pane opens a knock the moment somebody presses send on what they
   * wrote, and sends those words straight after. The doorman asks no opening
   * question then — it would land above words written without it — and its
   * first line is its reply. Absent or false: the opening is asked as before.
   */
  knocker_speaks_first: z.boolean().optional(),
});
export type KnockStartRequest = z.infer<typeof KnockStartRequest>;

/**
 * THE GATE (ruling R-0908-4, `docs/conversation-model.md` §3): where a knock
 * stands with the recipient, and the only lifecycle a knock has. Moved by the
 * recipient's verbs alone — never by the doorman, the clock or the sender — and
 * it never goes back to the door except through the thirty-second undo. Both
 * tiers read this one enum; a client-side copy is a drift waiting to happen.
 */
export const KnockGate = z.enum(["at_door", "let_in", "declined", "blocked"]);
export type KnockGate = z.infer<typeof KnockGate>;
